import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import H4 from '../H4'
import P from '../P'
import plusIcon from '../../../img/sym_plus.svg'
import minusIcon from '../../../img/sym_minus.svg'
import { useSearchParams } from 'react-router-dom'
import useIdStabile from '../useIdStabile'
import Highlighter from 'react-highlight-words'

interface CollapsableProps {
  title?: string
  text?: string
  textStyle?: React.CSSProperties
  titleStyle?: any
  headerStyle?: React.CSSProperties
  lightTheme?: boolean
  replaceTextContainer?: boolean
  children?: any
  buttonHeight?: string
  buttonWidth?: string
  buttonIconSize?: string;
  keywords?: string[]
  /**
   * Livello dell'intestazione dell'accordion. Default h3: nelle FAQ
   * l'accordion sta sotto un <h2> di sezione, e h2 -> h4 sarebbe un salto
   * di livello (4BJRC, WCAG 1.3.1).
   */
  headingLevel?: 'h2' | 'h3' | 'h4' | 'h5'
}

const defaultTitle = 'Messaggio pubblicitario con finalità promozionale'
const defaultText = 'QuintoPuoi è un prodotto di Banca Sistema S.p.A., Società appartenente al Gruppo Banca CF+, iscritto all’Albo dei Gruppi Bancari, e soggetta all’attività di direzione e coordinamento di Banca CF+ S.p.A.. Per informazioni economiche e contrattuali fare riferimento alle “Informazioni Europee di Base sul Credito ai Consumatori” (SECCI) che puoi richiedere presso tutte le filiali della Banca e presso gli agenti in attività finanziaria che operano su mandato diretto della Banca per la promozione e il collocamento del prodotto QuintoPuoi. La concessione del finanziamento è subordinata all’approvazione di Banca Sistema S.p.A..'

const Collapsable = ({title, text, textStyle = {}, headerStyle = {}, titleStyle = {}, lightTheme = false, replaceTextContainer = false, children, buttonWidth, buttonHeight, buttonIconSize, keywords = [], headingLevel = 'h3'}: CollapsableProps) => {

  const [showContent, setShowContent] = useState(false)
  const [urlSearchParams] = useSearchParams()

  // id univoci per collegare bottone e pannello (aria-controls / aria-labelledby).
  // Il suffisso viene da useId e non dal titolo: due Collapsable con lo stesso
  // titolo nella stessa pagina (il "Messaggio pubblicitario" di default ricorre
  // più volte) producevano id duplicati, e un id duplicato rende ambiguo il
  // riferimento aria-controls (WCAG 4.1.1 / 1.3.1).
  const uid = useIdStabile()
  const buttonId = `accordion-btn-${uid}`
  const panelId = `accordion-panel-${uid}`

  useEffect(() => {
    const termini = keywords.filter(k => !!k)
    if (!termini.length) return

    termini.forEach(k => {
      const search = k.toLowerCase()
      if ((title || defaultTitle).toLowerCase().includes(search) || (text || defaultText).toLowerCase().includes(search)) {
        setShowContent(true)
      }
    })
  }, [urlSearchParams, text, title])

  const messageButtonOnClick = (e: any) => {
    e.preventDefault()
    setShowContent(!showContent)
  }

  return (
    <Container lightTheme={lightTheme}>
      <Header style={headerStyle}>
        <StyledH4 as={headingLevel} lightTheme={lightTheme} style={titleStyle}>
          <MessageButton
            id={buttonId}
            lightTheme={lightTheme}
            type="button"
            onClick={messageButtonOnClick}
            aria-expanded={showContent}
            /* Solo a pannello aperto: quando e' chiuso il pannello non e' nel
               DOM, e un aria-controls che punta a un id inesistente e' un
               riferimento rotto. aria-expanded basta a comunicare lo stato. */
            aria-controls={showContent ? panelId : undefined}
          >
            <Highlighter autoEscape={true} searchWords={(urlSearchParams.get('ricerca') || '').trim().split(' ')} textToHighlight={title || defaultTitle} />
            <Icon aria-hidden="true" style={showContent ? minusIconStyle : plusIconStyle} buttonWidth={buttonWidth} buttonHeight={buttonHeight} buttonIconSize={buttonIconSize}/>
          </MessageButton>
        </StyledH4>
      </Header>
      {showContent &&
        (
          <Panel id={panelId} role="region" aria-labelledby={buttonId}>
          {replaceTextContainer ? children :
            <PromoMessageContainer>
              <PromoMessage lightTheme={lightTheme}>
                <div>
                  { children ? children :
                    <StyledP style={textStyle} lightTheme={lightTheme}>
                      {keywords ?
                        <Highlighter autoEscape={true} searchWords={keywords} textToHighlight={text || defaultText}/>
                        : (text || defaultText) }
                    </StyledP>
                  }
                </div>
              </PromoMessage>
            </PromoMessageContainer>
          }
          </Panel>
        )
      }
    </Container>
  )
}

// region Style
const plusIconStyle = {
  borderRadius: '50%',
  backgroundColor: '#FCBB00',
  backgroundImage: `url(${plusIcon})`,
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat'
}
const minusIconStyle = {
  borderRadius: '50%',
  backgroundColor: '#D8D8D8',
  backgroundImage: `url(${minusIcon})`,
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat'
}

const Container = styled.div<CollapsableProps>`
  background-color: ${props => props.lightTheme ? '#FFF' : '#2F2F2F'};
  width: 100%;
  border-left: 5px solid #FCBB00;
  text-align: left;
`
const Header = styled.div`
  min-height: 7rem;
  padding: 1.5rem 1.5rem 1.5rem 4rem;
  display: flex;
`
const MessageButton = styled.button<CollapsableProps>`
  width: 100%;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  /* Su main il titolo (un H4 maiuscolo, letter-spacing .25px) conteneva il
     bottone; ora e' il contrario, perche' l'intestazione deve stare fuori dal
     controllo. Il browser azzera text-transform e letter-spacing sui controlli
     di form, quindi il testo perdeva il maiuscolo: misurato su
     /landing-generale, "MESSAGGIO PUBBLICITARIO..." tornava minuscolo.
     Con inherit la resa e' identica a main. */
  text-transform: inherit;
  letter-spacing: inherit;

  &:focus {
    outline: none;
  }
  &:focus-visible {
    /* Sul tema scuro (footer delle landing e delle pagine di ringraziamento)
       #005fcc contro #222 vale 2.53:1, sotto la soglia di 3:1 di WCAG 1.4.11.
       Il bianco su quel fondo vale 15.3:1. */
    outline: 3px solid ${props => props.lightTheme ? '#005fcc' : '#fff'};
    outline-offset: 3px;
  }
`
const StyledH4 = styled(H4)<CollapsableProps>`
  color: ${props => props.lightTheme ? '#222' : '#FFF'};
  margin-bottom: 0;
  width: 100%;
`
const Panel = styled.div`
  width: 100%;
`
const PromoMessageContainer = styled.div`
  padding-left: 4rem;
  padding-right: 7rem;
  overflow: hidden;
`
const PromoMessage = styled.div<CollapsableProps>`
  display: block;
  background-color: ${props => props.lightTheme ? '#F5F5F5' : '#505050'};
  color: #F5F5F5;
  padding: 1.5rem 3rem;
  margin-bottom: 1.5rem;
`
const StyledP = styled(P)<CollapsableProps>`
  color: ${props => props.lightTheme ? '#222' : '#FFF'};
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.8;
  display: block;
`
const Icon = styled.div<any>`
  flex-shrink: 0;
  ${props => props.buttonWidth ? `width: ${props.buttonWidth}` : 'width: 4rem'};
  ${props => props.buttonHeight ? `height: ${props.buttonHeight}` : 'height: 4rem'};
  ${props => props.buttonIconSize ? `background-size: ${props.buttonIconSize}` : ''};
`
// endregion

export default Collapsable
